import type {
  AcceptanceCriteria,
  ConstraintConfig,
  PersonaOverride,
  QueryFamily,
  RecommendedPersona,
  ResearchPlan,
  SourceLaneStrategy,
} from '@/types/intelligence'

export type RecoverablePendingPlanSummary = {
  reportId: string
  query: string
  title: string
  isPublic: boolean
  status: 'planning' | 'awaiting_confirmation'
  currentPhase?: string
  statusMessage?: string
  updatedAt: Date | null
  planPreview: string
}

type RecordLike = Record<string, unknown>

const FALLBACK_GLOBAL_MINIMUMS = {
  signals_min: 1,
  claims_min: 1,
  entities_min: 1,
  relationships_min: 1,
  timeline_events_min: 1,
  datasets_min: 1,
  figures_min: 1,
  watchlist_triggers_min: 1,
  comparisons_min: 1,
}

function isRecordLike(value: unknown): value is RecordLike {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback
}

function readNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function readBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
}

function readNumberRecord(value: unknown): Record<string, number> {
  if (!isRecordLike(value)) return {}
  const result: Record<string, number> = {}

  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'number' && Number.isFinite(entry)) {
      result[key] = entry
    }
  }

  return result
}

function readDate(value: unknown): Date | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function normalizePersonaOverrides(value: unknown): PersonaOverride[] {
  if (!Array.isArray(value)) return []
  return value
    .map(item => {
      if (!isRecordLike(item)) return null
      return {
        if_persona_mode_in: readStringArray(item.if_persona_mode_in),
        must_include: readStringArray(item.must_include),
      }
    })
    .filter((item): item is PersonaOverride => Boolean(item))
}

function normalizeSourceLaneStrategy(value: unknown): SourceLaneStrategy[] {
  if (!Array.isArray(value)) return []
  return value
    .map(item => {
      if (!isRecordLike(item)) return null
      return {
        lane: readString(item.lane, 'general_research'),
        priority: readNumber(item.priority, 1),
        target_source_types: readStringArray(item.target_source_types),
        minimum_sources: readNumber(item.minimum_sources, 1),
      }
    })
    .filter((item): item is SourceLaneStrategy => Boolean(item))
}

function normalizeQueryFamilies(value: unknown): QueryFamily[] {
  if (!Array.isArray(value)) return []
  return value
    .map(item => {
      if (!isRecordLike(item)) return null
      const familyId = readString(item.family_id)
      if (!familyId) return null
      return {
        family_id: familyId,
        purpose: readString(item.purpose),
        queries: readStringArray(item.queries),
      }
    })
    .filter((item): item is QueryFamily => Boolean(item))
}

function normalizeConstraintConfig(value: unknown): ConstraintConfig {
  const config = isRecordLike(value) ? value : {}
  return {
    recency_priority: readBoolean(config.recency_priority, true),
    jurisdiction: readString(config.jurisdiction) || undefined,
    allowed_source_classes: readStringArray(config.allowed_source_classes),
    disallowed_source_classes: readStringArray(config.disallowed_source_classes),
    safety_notes: readStringArray(config.safety_notes),
  }
}

function normalizeAcceptanceCriteria(value: unknown): AcceptanceCriteria {
  const criteria = isRecordLike(value) ? value : {}
  return {
    ship_score_floor: readNumber(criteria.ship_score_floor, 0),
    evidence_requirements: readStringArray(criteria.evidence_requirements),
    hard_gates: readStringArray(criteria.hard_gates),
    quality_thresholds: readNumberRecord(criteria.quality_thresholds),
  }
}

function normalizeRecommendedPersona(value: unknown): RecommendedPersona | undefined {
  if (!isRecordLike(value)) return undefined
  const personaId = readString(value.persona_id)
  if (!personaId) return undefined
  const confidenceRaw = readString(value.confidence, 'MEDIUM').toUpperCase()
  const confidence =
    confidenceRaw === 'HIGH' || confidenceRaw === 'LOW' || confidenceRaw === 'MEDIUM'
      ? confidenceRaw
      : 'MEDIUM'
  return {
    persona_id: personaId,
    confidence,
    rationale: readString(value.rationale),
  }
}

function getRecoverableReportId(study: RecordLike): string {
  const directId =
    readString(study.intelligence_report_id) ||
    readString(study.report_id) ||
    readString(study.task_id)

  if (directId) return directId

  const rawId = readString(study.id)
  if (rawId.startsWith('ia-')) {
    return rawId.slice(3)
  }

  return rawId
}

export function collectRecoverablePendingPlans(rawStudies: unknown[]): RecoverablePendingPlanSummary[] {
  if (!Array.isArray(rawStudies)) return []

  const seenReportIds = new Set<string>()

  return rawStudies
    .map((study): RecoverablePendingPlanSummary | null => {
      if (!isRecordLike(study)) return null

      const reportId = getRecoverableReportId(study)
      if (!reportId || seenReportIds.has(reportId)) {
        return null
      }

      const status = readString(study.status)
      const currentPhase = readString(study.current_phase)
      const normalizedStatus =
        status === 'awaiting_confirmation'
          ? 'awaiting_confirmation'
          : status === 'planning'
            ? 'planning'
            : !status && currentPhase === 'PLAN'
              ? 'planning'
            : null

      if (!normalizedStatus) return null

      seenReportIds.add(reportId)

      const query = readString(
        study.query,
        readString(study.title, 'Recovered research plan')
      )
      const title = readString(study.title, query)
      const statusMessage = readString(study.status_message)

      return {
        reportId,
        query,
        title,
        isPublic: readBoolean(study.is_public),
        status: normalizedStatus,
        currentPhase: currentPhase || undefined,
        statusMessage: statusMessage || undefined,
        updatedAt: readDate(study.updated_at) ?? readDate(study.created_at),
        planPreview: statusMessage || title || query,
      }
    })
    .filter((study): study is RecoverablePendingPlanSummary => Boolean(study))
    .sort((left, right) => {
      const leftTime = left.updatedAt?.getTime() ?? 0
      const rightTime = right.updatedAt?.getTime() ?? 0
      return rightTime - leftTime
    })
}

export function normalizeResearchPlan(rawPlan: unknown, fallbackQuery: string): ResearchPlan {
  const plan = isRecordLike(rawPlan) ? rawPlan : {}
  const inputs = isRecordLike(plan.inputs) ? plan.inputs : {}
  const scenarioPlan = isRecordLike(plan.scenario_plan) ? plan.scenario_plan : {}
  const artifactTargets = isRecordLike(plan.artifact_targets) ? plan.artifact_targets : {}
  const globalMinimums = isRecordLike(artifactTargets.global_minimums)
    ? artifactTargets.global_minimums
    : {}
  const personaOverrides = isRecordLike(artifactTargets.persona_overrides)
    ? artifactTargets.persona_overrides
    : {}
  const researchQuestions = isRecordLike(plan.research_questions) ? plan.research_questions : {}
  const fallbackTimestamp = new Date().toISOString()
  const userPrompt = readString(inputs.user_prompt, fallbackQuery)
  const topicDescription = readString(inputs.topic_description, userPrompt || fallbackQuery)

  return {
    plan_id: readString(plan.plan_id, 'recovered-plan'),
    plan_summary: readString(plan.plan_summary, topicDescription || 'Research plan ready'),
    persona_notes: readString(plan.persona_notes) || undefined,
    created_at: readString(plan.created_at, fallbackTimestamp),
    updated_at: readString(plan.updated_at, readString(plan.created_at, fallbackTimestamp)),
    inputs: {
      user_prompt: userPrompt,
      persona_mode: readString(inputs.persona_mode, 'management_consultant'),
      persona_group: readString(inputs.persona_group, 'consultants'),
      topic_id: readString(inputs.topic_id, 'recovered-topic'),
      topic_description: topicDescription,
      topic_type: readString(inputs.topic_type, 'MARKET'),
      region: readString(inputs.region) || undefined,
      time_horizon: readString(inputs.time_horizon) || undefined,
      constraints: normalizeConstraintConfig(inputs.constraints),
    },
    scenario_plan: {
      axis_label: readString(scenarioPlan.axis_label, 'Key scenario axis'),
      source: readString(scenarioPlan.source, 'DERIVED') === 'LIBRARY' ? 'LIBRARY' : 'DERIVED',
      axis_template_id: readString(scenarioPlan.axis_template_id) || undefined,
      states_target: readNumber(scenarioPlan.states_target, 3),
      minimum_triggers: readNumber(scenarioPlan.minimum_triggers, 3),
      minimum_assumptions_per_state: readNumber(scenarioPlan.minimum_assumptions_per_state, 2),
    },
    artifact_targets: {
      global_minimums: {
        signals_min: readNumber(globalMinimums.signals_min, FALLBACK_GLOBAL_MINIMUMS.signals_min),
        claims_min: readNumber(globalMinimums.claims_min, FALLBACK_GLOBAL_MINIMUMS.claims_min),
        entities_min: readNumber(globalMinimums.entities_min, FALLBACK_GLOBAL_MINIMUMS.entities_min),
        relationships_min: readNumber(globalMinimums.relationships_min, FALLBACK_GLOBAL_MINIMUMS.relationships_min),
        timeline_events_min: readNumber(globalMinimums.timeline_events_min, FALLBACK_GLOBAL_MINIMUMS.timeline_events_min),
        datasets_min: readNumber(globalMinimums.datasets_min, FALLBACK_GLOBAL_MINIMUMS.datasets_min),
        figures_min: readNumber(globalMinimums.figures_min, FALLBACK_GLOBAL_MINIMUMS.figures_min),
        watchlist_triggers_min: readNumber(
          globalMinimums.watchlist_triggers_min,
          FALLBACK_GLOBAL_MINIMUMS.watchlist_triggers_min
        ),
        comparisons_min: readNumber(globalMinimums.comparisons_min, FALLBACK_GLOBAL_MINIMUMS.comparisons_min),
      },
      persona_overrides: {
        required_blocks: readStringArray(personaOverrides.required_blocks),
        persona_specific_blocks: normalizePersonaOverrides(personaOverrides.persona_specific_blocks),
      },
    },
    research_questions: {
      decision_questions: readStringArray(researchQuestions.decision_questions),
      mechanism_questions: readStringArray(researchQuestions.mechanism_questions),
      evidence_questions: readStringArray(researchQuestions.evidence_questions),
      counterpoint_questions: readStringArray(researchQuestions.counterpoint_questions),
      data_questions: readStringArray(researchQuestions.data_questions),
      scenario_questions: readStringArray(researchQuestions.scenario_questions),
    },
    source_lane_strategy: normalizeSourceLaneStrategy(plan.source_lane_strategy),
    query_families: normalizeQueryFamilies(plan.query_families),
    acceptance_criteria: normalizeAcceptanceCriteria(plan.acceptance_criteria),
    recommended_persona: normalizeRecommendedPersona(plan.recommended_persona),
  }
}
